import React, { useState } from "react"
import DineroFactory from "dinero.js"
import { Link } from "react-router-dom"
import * as graphql from "lib/types/generated/graphql-types"
import { Order, OrderHistory } from "lib/types/generated/graphql-types"
import { ReactComponent as ChevronDown } from "images/icons/chevron-down.svg"
import { ReactComponent as ChevronUp } from "images/icons/chevron-up.svg"
import { ReactComponent as CheckCircle } from "images/icons/circle-check.svg"

import {
  Container,
  Content,
  ExpandedSubTotalWrapper,
  ExpandedTotalWrapper,
  Footer,
  Header,
  Id,
  ImageAndDetails,
  ImageContainer,
  Name,
  OrderLineTotal,
  ParentId,
  ProductImage,
  VatWrapper
} from "./OrderLine.styled"
import Money from "../../Money"
import { handleImageError } from "helpers/image"
import Operations from "./Operations/Operations"
import CopyButton from "../../Ui/CopyButton"
import GiftOperations from "./GiftOperations"

type Props = {
  gift: graphql.OrderGift
  order: Order
  orderHistory: OrderHistory
}

export const GiftLine = ({ gift, order, orderHistory }: Props) => {
  const [showFullSummary, setShowFullSummary] = useState<boolean>(false)

  if (!gift) return null

  const hasDiscountPrice = gift.salePriceAmount < gift.basePriceAmount

  const showCustomerHasPaid = order.isTaxIncludedInPrice && order.Compensations.length === 0

  return (
    <Container>
      <Header onClick={() => setShowFullSummary(!showFullSummary)}>
        {showFullSummary ? <ChevronUp /> : <ChevronDown />}
      </Header>
      <Content $showFullSummary={showFullSummary} $showCustomerHasPaid={showCustomerHasPaid}>
        <ImageAndDetails>
          <ImageContainer $showFullSummary={showFullSummary}>
            <ProductImage
              $showFullSummary={showFullSummary}
              src={gift.imageUrl as string}
              onError={handleImageError}
            />
          </ImageContainer>
          <div>
            <Name>{gift.displayName}</Name>
            <Id>
              <b>Variant Id: </b>
              <div>{gift.productVariantId}</div>
              <CopyButton string={gift.productVariantId} />
            </Id>
            <Id>
              <ParentId>
                <b>Parent Id:</b>{" "}
                <Link to={`/products/${gift.productParentId}`}>{gift.productParentId}</Link>
                <CopyButton string={gift.productParentId} />
              </ParentId>
            </Id>
          </div>
        </ImageAndDetails>
        {!showFullSummary ? (
          <>
            <OrderLineTotal>
              Gift line total{" "}
              <b>
                <Money
                  amount={gift.salePriceAmount * gift.quantity}
                  currencyUnit={order.currencyCode as DineroFactory.Currency}
                />
              </b>
            </OrderLineTotal>
            <OrderLineTotal>
              Customer has paid{" "}
              <b>
                <CheckCircle />
                <Money amount={0} currencyUnit={order.currencyCode as DineroFactory.Currency} />
              </b>
            </OrderLineTotal>
          </>
        ) : (
          <div>
            <ExpandedTotalWrapper>
              <div>Variant base price</div>
              <Money
                amount={gift.basePriceAmount}
                currencyUnit={order.currencyCode as DineroFactory.Currency}
              />
            </ExpandedTotalWrapper>
            {hasDiscountPrice && (
              <ExpandedTotalWrapper>
                <div>Variant sale price</div>
                <Money
                  amount={gift.salePriceAmount}
                  currencyUnit={order.currencyCode as DineroFactory.Currency}
                />
              </ExpandedTotalWrapper>
            )}
            <ExpandedTotalWrapper>
              <div>Quantity</div>
              <div>{gift.quantity}</div>
            </ExpandedTotalWrapper>
            <ExpandedSubTotalWrapper>
              <div>Subtotal</div>
              <Money
                amount={gift.salePriceAmount * gift.quantity}
                currencyUnit={order.currencyCode as DineroFactory.Currency}
              />
            </ExpandedSubTotalWrapper>
          </div>
        )}
      </Content>
      {showFullSummary && (
        <Operations
          order={order}
          orderLineId={gift.id}
          salePriceAmount={gift.salePriceAmount * gift.quantity}
        />
      )}
      {showFullSummary && (
        <>
          <VatWrapper>
            <span>
              VAT {(gift.taxPercentage || 0) / Math.pow(10, gift.taxPercentageDecimals || 0)}%
            </span>
            <Money
              amount={gift.totalTaxAmount}
              currencyUnit={order.currencyCode as DineroFactory.Currency}
            />
          </VatWrapper>
          <OrderLineTotal>
            Gift line total{" "}
            <b>
              <Money
                amount={gift.salePriceAmount * gift.quantity}
                currencyUnit={order.currencyCode as DineroFactory.Currency}
              />
            </b>
          </OrderLineTotal>
          <OrderLineTotal>
            Customer has paid{" "}
            <b>
              <CheckCircle />
              <Money amount={0} currencyUnit={order.currencyCode as DineroFactory.Currency} />
            </b>
          </OrderLineTotal>
        </>
      )}
      <Footer>
        <GiftOperations gift={gift} orderHistory={orderHistory} order={order} />
      </Footer>
    </Container>
  )
}
